/**
 * Auth Store - Zustand store for authentication state
 * Manages JWT tokens, login/logout, and session state
 */

import { STORAGE_KEYS } from "@lib/config/storageKeys";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { queryClient } from "@/main";
import { API_ENDPOINTS } from "../config/constants";
import { useFilialeContextStore } from "../stores/filialeContext.store";
import { usePermissionStore } from "../stores/permissions.store";

/**
 * How long the client waits for the server to acknowledge the revocation.
 *
 * Bounded on purpose: the UI never blocks on this call, but an unbounded request
 * would keep a socket — and the closure holding the pre-logout token — alive for
 * as long as the server stays silent. 5s is orders of magnitude above a local
 * round-trip and far below anything a user would read as "the app is stuck".
 */
const LOGOUT_REVOCATION_TIMEOUT_MS = 5000;

interface JwtPayload {
  readonly id?: number;
  readonly roles?: string[];
  readonly exp?: number;
  readonly iat?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface AuthState {
  token: string | null;
  refreshToken: string | null;
  qrCodeToken: string | null;
  isLoggedIn: boolean;
  roles: string[];
  userId: number | null;
  email?: string;
  prenom?: string;
  nom?: string;
  avatar?: { id: number; storagePath?: string } | null;
}

export interface AuthActions {
  login: (token: string, refreshToken?: string) => void;
  loginQrCode: (token: string) => void;
  logout: () => Promise<void>;
  logoutQrCode: () => void;
  setUserId: (userId: number) => void;
}

export type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      qrCodeToken: null,
      isLoggedIn: false,
      roles: [],
      userId: null,

      login: (token, refreshToken) => {
        const decoded = jwtDecode<JwtPayload>(token);

        // Defensive guard: a token without an `id` claim is not a Lexik API JWT
        // (e.g. a Mercure subscriber JWT). Never let it overwrite an existing
        // authenticated session — that would wipe userId/refreshToken and break
        // every subsequent API call.
        if (decoded.id === undefined && get().isLoggedIn) {
          console.warn(
            "authStore.login: refusing to overwrite an authenticated session with a token lacking an `id` claim",
          );
          return;
        }

        set({
          token,
          refreshToken: refreshToken ?? null,
          isLoggedIn: true,
          roles: decoded.roles || [],
          userId: decoded.id || null,
          email: decoded.email,
          prenom: decoded.prenom,
          nom: decoded.nom,
        });
      },

      loginQrCode: (token) => {
        set({ qrCodeToken: token });
      },

      /**
       * Wipe the session locally, then ask the server to revoke it.
       *
       * The ORDER is the contract: the local purge runs to completion before the
       * first `await`, so every caller keeps the behaviour it had when this action
       * was synchronous — the UI leaves the authenticated state in the same tick.
       * The network only gets a say once nothing is waiting on it.
       *
       * The revocation is best-effort and its errors are swallowed on purpose.
       * Stranding a user on a login screen because a DELETE did not land is a
       * worse outcome than a refresh token the server did not hear about, and that
       * token dies by itself at `gesdinet_jwt_refresh_token.ttl`. The returned
       * promise therefore always RESOLVES: `await logout()` is safe to write and
       * can never produce an unhandled rejection at a fire-and-forget call site.
       */
      logout: async () => {
        // Read BEFORE the purge — this is the only moment the token is in hand,
        // and it is what identifies the session to revoke server-side.
        const sessionToken = get().token;

        set({
          token: null,
          refreshToken: null,
          qrCodeToken: null,
          isLoggedIn: false,
          roles: [],
          userId: null,
        });
        // Clear permissions on logout
        usePermissionStore.getState().clear();
        // Reset filiale context selection on logout
        useFilialeContextStore.getState().reset();
        // Invalidate all TanStack Query caches so next login gets fresh data
        queryClient.clear();

        // No token means no session, and nothing the server could scope a
        // revocation to: the purge above is the whole job in that case.
        if (!sessionToken) return;

        try {
          // Raw `axios`, deliberately not the `api` instance, for two reasons that
          // both bite on exactly this call:
          //  - the response interceptor turns ANY 401 into a refresh-token attempt,
          //    so a logout answering 401 would drive straight back into this store
          //    instead of finishing;
          //  - the request interceptor re-reads the token from the store, which the
          //    purge above has just emptied, so the request would go out anonymous.
          // It also keeps `authStore` from importing `client.ts`, which imports
          // this module. `/api` is spelled out because a raw axios call has no
          // baseURL — hence the constant staying prefix-free like its neighbours.
          await axios.post(`/api${API_ENDPOINTS.AUTH.LOGOUT}`, null, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            timeout: LOGOUT_REVOCATION_TIMEOUT_MS,
          });
        } catch (error) {
          // Swallowed on purpose, but NOT silent: the local session is already
          // gone and the user is already on the login screen, so there is nothing
          // actionable for them — but an operator reading the console needs to be
          // able to tell "revoked" from "the DELETE never arrived".
          console.warn("[authStore] logout: local session purged, server-side revocation failed", {
            error,
          });
        }
      },

      logoutQrCode: () => {
        set({ qrCodeToken: null });
      },

      setUserId: (userId) => set({ userId }),
    }),
    {
      name: STORAGE_KEYS.AUTH.TOKEN,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

/**
 * Synchronous read from localStorage for auth state
 * Used in loaderDeps (runs before beforeLoad, can't use Zustand async rehydrate)
 */
export function readAuthFromStorage(): {
  userId: number | null;
  token: string | null;
} {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.AUTH.TOKEN);
    if (!stored) return { userId: null, token: null };

    const parsed = JSON.parse(stored);
    // Zustand persist wraps state in { state: {...}, version: N }
    const state = parsed?.state ?? parsed;

    return {
      userId: state?.userId ?? null,
      token: state?.token ?? null,
    };
  } catch {
    return { userId: null, token: null };
  }
}

// Re-export for backward compatibility
export default useAuthStore;
